import { CopyOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { App as AntApp, Button, Card, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { emptyWorkflowGraph } from '@gw/shared';
import { useDeleteWorkflow, useDuplicateWorkflow, useSaveWorkflow, useWorkflows } from '../api/hooks';
import { extractErrorMessage } from '../api/client';
import type { Workflow } from '../api/types';

const OUTPUT_LABEL: Record<string, string> = { text: 'Văn bản', image: 'Ảnh', video: 'Video' };

/**
 * Danh sach WORKFLOW MODEL. Voi phan mem thu 3, moi workflow xuat hien trong
 * /api/v1/catalog va duoc goi y het mot nhom model.
 */
export default function WorkflowsPage() {
  const { message } = AntApp.useApp();
  const navigate = useNavigate();
  const list = useWorkflows();
  const save = useSaveWorkflow();
  const del = useDeleteWorkflow();
  const dup = useDuplicateWorkflow();

  const onCreate = async () => {
    const stamp = Date.now().toString(36);
    try {
      const w = await save.mutateAsync({
        data: {
          slug: `workflow-${stamp}`,
          name: 'Workflow mới',
          status: 'disabled',
          graph: emptyWorkflowGraph(),
          paramsSchema: [],
          retryCount: 0,
        },
      });
      navigate(`/workflows/${w.id}`);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Workflow model
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} loading={save.isPending} onClick={onCreate}>
          Tạo workflow
        </Button>
      </Space>

      <Card>
        <Typography.Paragraph type="secondary">
          <b>Workflow model</b> chạy nhiều bước nối tiếp (model / nhóm model / node chức năng). Với phần mềm thứ 3
          nó giống hệt một nhóm model: có trong <code>/api/v1/catalog</code>, gửi input qua <code>POST /api/v1/jobs</code>,
          nhận kết quả qua poll hoặc webhook — <b>không cần cập nhật tài liệu API</b>.
        </Typography.Paragraph>

        <Table<Workflow>
          rowKey="id"
          loading={list.isLoading}
          dataSource={list.data || []}
          columns={[
            { title: 'Tên', dataIndex: 'name', render: (v, r) => <a onClick={() => navigate(`/workflows/${r.id}`)}>{v}</a> },
            { title: 'Slug', dataIndex: 'slug', render: (v) => <code>{v}</code> },
            { title: 'Danh mục', dataIndex: 'category', render: (v) => v || '—' },
            { title: 'Số node', dataIndex: 'nodeCount', width: 90 },
            {
              title: 'Đầu ra',
              dataIndex: 'outputType',
              width: 100,
              render: (v: string) => <Tag>{OUTPUT_LABEL[v] || v}</Tag>,
            },
            {
              title: 'Chạy lại khi lỗi',
              dataIndex: 'retryCount',
              width: 130,
              render: (v: number) => (v ? `${v} lần` : '—'),
            },
            {
              title: 'Trạng thái',
              dataIndex: 'status',
              width: 110,
              render: (v: string) =>
                v === 'active' ? <Tag color="success">Đang bật</Tag> : <Tag>Đang tắt</Tag>,
            },
            {
              title: 'Thao tác',
              width: 200,
              render: (_, r) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/workflows/${r.id}`)}>
                    Sửa
                  </Button>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={async () => {
                      try {
                        await dup.mutateAsync(r.id);
                        message.success('Đã nhân bản');
                      } catch (e) {
                        message.error(extractErrorMessage(e));
                      }
                    }}
                  >
                    Nhân bản
                  </Button>
                  <Popconfirm
                    title="Xóa workflow này?"
                    okText="Xóa"
                    cancelText="Hủy"
                    onConfirm={async () => {
                      try {
                        await del.mutateAsync(r.id);
                        message.success('Đã xóa');
                      } catch (e) {
                        message.error(extractErrorMessage(e));
                      }
                    }}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
